/** Everything an editor row needs to show and change a voice identity.
 *
 * Both editors ask the same three questions — which original diarization label
 * is this row, who is it currently attributed to, and what did the model
 * suggest — so the wiring lives here once rather than twice. */

import { useCallback, useMemo } from 'react';
import { AxiosError } from 'axios';
import {
  useVoiceIdentifications,
  useVoiceIdentificationTimeline
} from '@utils/api/editors/voice-identifications';
import {
  VoiceIdentification,
  VoiceIdentificationStatus,
  VoiceTimelineSpan
} from '@utils/types/organization/voice-profile.ts';
import {
  groupTimelineByLabel,
  resolveSpeakerLabelAt,
  TimelineLabel
} from '@utils/editors/voice-sample-ranges.ts';

export interface VoiceIdentityState {
  /** True while either request is in flight. */
  isLoading: boolean;
  /** Source diarization windows, keyed by original label. */
  timeline: VoiceTimelineSpan[];
  labels: TimelineLabel[];
  /** True when the document has no source diarization at all (409). The UI
   *  explains this instead of opening an empty selection flow. */
  noDiarization: boolean;
  status: VoiceIdentificationStatus;
  statusReason: string | null;
  /** Per original label. */
  byLabel: Map<string, VoiceIdentification>;
  /** The original label heard at a moment in the source audio. */
  labelAt: (startMs: number, stopMs?: number) => string | null;
  /** The decision/suggestion for the row at that moment. */
  identificationAt: (
    startMs: number,
    stopMs?: number
  ) => VoiceIdentification | undefined;
}

export const useVoiceIdentity = (
  sessionId: string | undefined,
  documentId: string | undefined,
  /** Pass false on surfaces that must not query (shared links, read-only). */
  enabled: boolean
): VoiceIdentityState => {
  const timelineQuery = useVoiceIdentificationTimeline(
    sessionId,
    documentId,
    enabled
  );
  const identificationsQuery = useVoiceIdentifications(
    sessionId,
    documentId,
    enabled
  );

  const timeline = useMemo(
    () => timelineQuery.data ?? [],
    [timelineQuery.data]
  );
  const labels = useMemo(() => groupTimelineByLabel(timeline), [timeline]);

  const byLabel = useMemo(() => {
    const map = new Map<string, VoiceIdentification>();
    for (const row of identificationsQuery.data?.speakers ?? []) {
      map.set(row.speaker_label, row);
    }
    return map;
  }, [identificationsQuery.data]);

  const labelAt = useCallback(
    (startMs: number, stopMs?: number) =>
      resolveSpeakerLabelAt(timeline, startMs, stopMs),
    [timeline]
  );

  const identificationAt = useCallback(
    (startMs: number, stopMs?: number) => {
      const label = resolveSpeakerLabelAt(timeline, startMs, stopMs);
      return label ? byLabel.get(label) : undefined;
    },
    [timeline, byLabel]
  );

  // A 409 means the source has no diarization; any other failure is just an
  // unavailable timeline, which must not be reported as "no speakers found".
  const noDiarization =
    (timelineQuery.error as AxiosError | null)?.response?.status === 409 ||
    (timelineQuery.isSuccess && timeline.length === 0);

  return {
    isLoading: timelineQuery.isLoading || identificationsQuery.isLoading,
    timeline,
    labels,
    noDiarization,
    status: identificationsQuery.data?.status ?? 'not_requested',
    statusReason: identificationsQuery.data?.reason ?? null,
    byLabel,
    labelAt,
    identificationAt
  };
};
